import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Chip,
  Divider,
  Tooltip,
  alpha,
  Avatar,
  LinearProgress,
  IconButton,
  Badge
} from '@mui/material';
import {
  Description as FileIcon,
  Group as TeamIcon,
  AccountBalance as BankIcon,
  Business as CompanyIcon,
  Person as PersonIcon,
  Event as EventIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  CalendarToday as CalendarIcon,
  Assignment as TaskIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import api from '../utils/axios';
import Calendar from './Calendar';
import RSSFeeds from './tools/RSSFeeds';

interface Metrics {
  totalClaims: number;
  openClaims: number;
  closedClaims: number;
  pendingClaims: number;
  totalContacts: number;
  totalInsurers: number;
  totalCompanies: number;
  totalClients: number;
  totalEvents: number;
  totalAmount: number;
  avgSettlementTime: number;
  successRate: number;
  recentClaims: number;
  overdueClaims: number;
}

interface Claim {
  id: string;
  claimNumber: string;
  status: string;
  clientName: string;
  insurerName: string;
  createdAt: string;
  amount?: number;
}

const DashboardAlternative: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [metricsResponse, claimsResponse] = await Promise.all([
          api.get<{ data: Metrics }>('/api/metrics'),
          api.get<{ data: Claim[] }>('/api/claims', {
            params: {
              limit: 10,
              sort: 'createdAt:desc'
            }
          })
        ]);
        setMetrics(metricsResponse.data.data);
        setClaims(claimsResponse.data.data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        // Set default values if API fails
        setMetrics({
          totalClaims: 0,
          openClaims: 0,
          closedClaims: 0,
          pendingClaims: 0,
          totalContacts: 0,
          totalInsurers: 0,
          totalCompanies: 0,
          totalClients: 0,
          totalEvents: 0,
          totalAmount: 0,
          avgSettlementTime: 0,
          successRate: 0,
          recentClaims: 0,
          overdueClaims: 0
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OPEN':
        return 'primary';
      case 'CLOSED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'OVERDUE':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    // Re-fetch data
    const fetchData = async () => {
      try {
        const [metricsResponse, claimsResponse] = await Promise.all([
          api.get<{ data: Metrics }>('/api/metrics'),
          api.get<{ data: Claim[] }>('/api/claims', {
            params: {
              limit: 10,
              sort: 'createdAt:desc'
            }
          })
        ]);
        setMetrics(metricsResponse.data.data);
        setClaims(claimsResponse.data.data);
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Failed to refresh dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box p={3}>
        <Card>
          <CardContent>
            <Typography>Failed to load dashboard data. Please try again later.</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%', 
      bgcolor: '#f8fafc', 
      minHeight: '100vh',
      py: 3,
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header with Actions */}
      <Box sx={{ 
        mb: 4,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ 
            fontWeight: 800, 
            color: '#1e293b',
            mb: 0.5,
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' }
          }}>
            Analytics Dashboard
          </Typography>
          <Typography variant="body2" color="#64748b" sx={{ fontWeight: 500 }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh data" arrow>
            <IconButton 
              onClick={handleRefresh}
              sx={{ 
                bgcolor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                '&:hover': { bgcolor: '#f1f5f9' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Notifications" arrow>
            <IconButton sx={{ 
              bgcolor: 'white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              '&:hover': { bgcolor: '#f1f5f9' }
            }}>
              <Badge badgeContent={3} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Key Performance Indicators */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              pointerEvents: 'none'
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  Total Claims
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 40, 
                  height: 40 
                }}>
                  <FileIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {metrics.totalClaims || 0}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  +{metrics.recentClaims || 0} this month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              pointerEvents: 'none'
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  Active Claims
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 40, 
                  height: 40 
                }}>
                  <WarningIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {metrics.openClaims || 0}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  {metrics.overdueClaims || 0} overdue
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              pointerEvents: 'none'
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  Success Rate
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 40, 
                  height: 40 
                }}>
                  <CheckCircleIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {metrics.successRate || 0}%
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  +2.5% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} lg={3}>
          <Card sx={{ 
            height: '100%',
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 100%)',
              pointerEvents: 'none'
            }
          }}>
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 600 }}>
                  Total Value
                </Typography>
                <Avatar sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  width: 40, 
                  height: 40 
                }}>
                  <MoneyIcon />
                </Avatar>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1 }}>
                {formatCurrency(metrics.totalAmount || 0)}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <ArrowUpwardIcon sx={{ fontSize: 16, color: '#4ade80' }} />
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  +12.3% vs last month
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Progress Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ 
            height: '100%',
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Claims Progress Overview
                </Typography>
                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                        Open Claims
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.openClaims || 0}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={metrics.totalClaims ? (metrics.openClaims / metrics.totalClaims) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#3b82f6',
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                  
                  <Box sx={{ mb: 3 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                        Closed Claims
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.closedClaims || 0}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={metrics.totalClaims ? (metrics.closedClaims / metrics.totalClaims) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#10b981',
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                  
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                        Pending Claims
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b' }}>
                        {metrics.pendingClaims || 0}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      variant="determinate" 
                      value={metrics.totalClaims ? (metrics.pendingClaims / metrics.totalClaims) * 100 : 0}
                      sx={{ 
                        height: 8, 
                        borderRadius: 4,
                        bgcolor: '#e2e8f0',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: '#f59e0b',
                          borderRadius: 4
                        }
                      }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ 
                    p: 3, 
                    bgcolor: '#f8fafc', 
                    borderRadius: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', mb: 1 }}>
                      {metrics.avgSettlementTime || 0}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
                      Average Days to Settlement
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SpeedIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
                      <Typography variant="body2" sx={{ color: '#64748b' }}>
                        Target: 30 days
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={4}>
          <Card sx={{ 
            height: '100%',
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
                Quick Stats
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#dbeafe', width: 32, height: 32 }}>
                      <PersonIcon sx={{ color: '#3b82f6', fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Contacts
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {metrics.totalContacts || 0}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#dcfce7', width: 32, height: 32 }}>
                      <BankIcon sx={{ color: '#10b981', fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Insurers
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {metrics.totalInsurers || 0}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#fef3c7', width: 32, height: 32 }}>
                      <CompanyIcon sx={{ color: '#f59e0b', fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Companies
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {metrics.totalCompanies || 0}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{ bgcolor: '#f3e8ff', width: 32, height: 32 }}>
                      <TeamIcon sx={{ color: '#8b5cf6', fontSize: 16 }} />
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#64748b' }}>
                      Clients
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                    {metrics.totalClients || 0}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity and Calendar */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card sx={{ 
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Recent Claims Activity
                </Typography>
                <Tooltip title="View all claims" arrow>
                  <Link href="/claims" color="primary" sx={{ textDecoration: 'none', fontWeight: 600 }}>
                    View All
                  </Link>
                </Tooltip>
              </Box>
              
              <TableContainer component={Paper} sx={{ 
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                boxShadow: 'none'
              }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Claim #</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Insurer</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#64748b' }}>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {claims.length > 0 ? (
                      claims.map((claim, index) => (
                        <TableRow 
                          key={claim.id}
                          sx={{ 
                            '&:hover': { bgcolor: '#f8fafc' },
                            bgcolor: index % 2 === 0 ? 'transparent' : '#f8fafc'
                          }}
                        >
                          <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {claim.claimNumber || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={claim.status || 'UNKNOWN'} 
                              color={getStatusColor(claim.status) as any}
                              size="small"
                              sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#64748b' }}>{claim.clientName || 'N/A'}</TableCell>
                          <TableCell sx={{ color: '#64748b' }}>{claim.insurerName || 'N/A'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {claim.amount ? formatCurrency(claim.amount) : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b' }}>
                            {claim.createdAt ? new Date(claim.createdAt).toLocaleDateString() : 'N/A'}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Typography color="#64748b" variant="body1">
                            No claims found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={4}>
          <Card sx={{ 
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3,
            height: '100%'
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  Today's Schedule
                </Typography>
                <Tooltip title="View full calendar" arrow>
                  <Link href="/calendar" color="primary" sx={{ textDecoration: 'none', fontWeight: 600 }}>
                    Calendar
                  </Link>
                </Tooltip>
              </Box>
              
              <Box sx={{ 
                height: 'calc(100% - 60px)', 
                overflow: 'auto',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                p: 2
              }}>
                <Calendar isWidget={true} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions and News */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
                Quick Actions
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#e0f2fe',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <FileIcon sx={{ color: '#0284c7', fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#0c4a6e' }}>
                      New Claim
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: '#f0fdf4',
                    border: '1px solid #bbf7d0',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#dcfce7',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <PersonIcon sx={{ color: '#16a34a', fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#14532d' }}>
                      Add Client
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: '#fef7ed',
                    border: '1px solid #fed7aa',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#ffedd5',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <EventIcon sx={{ color: '#ea580c', fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#7c2d12' }}>
                      Schedule Event
                    </Typography>
                  </Card>
                </Grid>
                
                <Grid item xs={6}>
                  <Card sx={{ 
                    p: 2, 
                    textAlign: 'center',
                    bgcolor: '#fdf4ff',
                    border: '1px solid #f3e8ff',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: '#faf5ff',
                      transform: 'translateY(-2px)'
                    }
                  }}>
                    <TaskIcon sx={{ color: '#9333ea', fontSize: 32, mb: 1 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#581c87' }}>
                      Create Task
                    </Typography>
                  </Card>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} lg={6}>
          <Card sx={{ 
            background: 'white',
            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
            borderRadius: 3
          }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 3 }}>
                Industry News & Updates
              </Typography>
              
              <Box sx={{ 
                height: 'calc(100% - 60px)', 
                overflow: 'auto',
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                p: 2
              }}>
                <RSSFeeds isWidget={true} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardAlternative; 